/**
 * Organization member-management wire types + fetchers.
 *
 * BE source: ``ando-agent/src/api/v1/endpoints/org_management.py`` —
 * routes mounted under ``/api/v1/org/members`` (admin-protected
 * GET / POST / PUT / DELETE plus the per-member tier override) and
 * ``/api/v1/org/invites`` (admin-protected GET / DELETE for pending
 * invitations).
 *
 * Role policy mirrored on the BE:
 *   - ``GET /org/members``  → any org member; the BE strips
 *     admin-only fields (storage_limit_mb_override, cost_cap_override,
 *     usage_multiplier_override, invited_email, invited_by, …) for
 *     non-admin callers, so those fields are typed optional here.
 *   - ``POST /org/members`` → admin/owner only.
 *   - ``PUT  /org/members/{user_id}`` (role change) → owner only.
 *   - ``PUT  /org/members/{user_id}/tier`` (plan override) → admin/owner.
 *   - ``DELETE /org/members/{user_id}`` → admin/owner.
 *   - ``GET / DELETE /org/invites`` → admin/owner.
 *
 * The shapes below are intentionally permissive — the BE returns plain
 * SQLAlchemy row dicts with all timestamps as ISO strings. We type only
 * the fields the FE actually renders; extra fields pass through.
 */

import type { OrgRole } from "../../domain/user.js";

import { apiClient } from "../ApiClient.js";

/**
 * ``OrgRole`` is intentionally NOT re-exported from this module.
 *
 * The canonical definition lives in ``domain/user.ts`` (shared with the
 * login / topbar slices) and is already re-exported through
 * ``endpoints/adminOrgs.ts``. Re-exporting it a second time would
 * trigger ``TS2308`` ambiguous-export at the endpoints barrel.
 * Callers still import it from ``@ando/core`` as before — this file
 * only uses the type internally.
 */

/**
 * Member row returned by ``GET /org/members``.
 *
 * Admin-only fields (tier override, storage / cost caps, invite trail)
 * are stripped for non-admin callers — see ``_MEMBER_SAFE_KEYS`` in
 * ``org_management.py``. They're typed optional here so the same
 * interface serves both audiences without an extra discriminator.
 */
export interface OrgMember {
  readonly id: string;
  readonly org_id: string;
  /** ``null`` for pending invites that never resolved to a user account. */
  readonly user_id: string | null;
  readonly role: OrgRole;
  readonly is_active: boolean;
  /** ISO-8601 timestamp. ``null`` for never-joined pending invites. */
  readonly joined_at: string | null;
  readonly email: string | null;
  readonly name: string | null;
  // ── admin-only (present iff caller is admin/owner) ────────────────────
  readonly invited_at?: string | null;
  readonly invited_email?: string | null;
  readonly invited_by?: string | null;
  readonly updated_at?: string | null;
  readonly usage_multiplier_override?: number | null;
  readonly storage_limit_mb_override?: number | null;
  readonly cost_cap_override?: number | null;
}

/**
 * Pending invite row returned by ``GET /org/invites`` and by ``POST
 * /org/members`` when the invitee has no user account yet.
 *
 * The BE persists pending invites as "ghost" ``organization_members``
 * rows (``user_id IS NULL``, ``is_active=False``, ``joined_at IS NULL``)
 * — same wire shape as ``OrgMember`` with ``invited_email`` populated.
 */
export interface OrgInvite {
  readonly id: string;
  readonly org_id: string;
  readonly role: OrgRole;
  readonly invited_email: string | null;
  readonly invited_at: string | null;
  readonly invited_by: string | null;
  readonly is_active: false;
  readonly joined_at: null;
  readonly user_id: null;
}

export interface InviteMemberRequest {
  readonly email: string;
  readonly role: OrgRole;
}

/**
 * Discriminated response shape for ``POST /org/members``.
 *
 *   - ``status: "added"``   → the email matched an existing user; member
 *                              row created immediately.
 *   - ``status: "invited"`` → no user account yet; pending invite row
 *                              created instead.
 */
export type InviteMemberResponse =
  | { readonly status: "added"; readonly email: string; readonly member: OrgMember }
  | { readonly status: "invited"; readonly email: string; readonly invite: OrgInvite };

export interface UpdateMemberRoleRequest {
  readonly role: OrgRole;
}

export interface UpdateMemberTierRequest {
  /** ``null`` clears the override and the member inherits the org tier. */
  readonly usage_multiplier_override: number | null;
}

/** Effective connector entitlement returned by ``GET /org/connectors/mine``. */
export interface MyConnectorAccess {
  readonly connector_key: string;
  readonly display_name: string;
  readonly category: string;
  readonly assignment_mode: "all_members" | "per_user";
  readonly is_assigned: boolean;
  readonly can_toggle: boolean;
}

/** Connector capabilities the current member may use. */
export async function listMyConnectorAccess(): Promise<readonly MyConnectorAccess[]> {
  return apiClient.get<readonly MyConnectorAccess[]>("/org/connectors/mine");
}

/** ``GET /org/members`` — any org member. */
export async function listOrgMembers(): Promise<readonly OrgMember[]> {
  return apiClient.get<readonly OrgMember[]>("/org/members");
}

/** ``POST /org/members`` — admin/owner only. */
export async function inviteOrgMember(
  body: InviteMemberRequest,
): Promise<InviteMemberResponse> {
  return apiClient.post<InviteMemberResponse>("/org/members", body);
}

/** ``PUT /org/members/{user_id}`` — owner only. */
export async function updateOrgMemberRole(
  userId: string,
  role: OrgRole,
): Promise<OrgMember> {
  return apiClient.put<OrgMember>(
    `/org/members/${encodeURIComponent(userId)}`,
    { role } satisfies UpdateMemberRoleRequest,
  );
}

/** ``PUT /org/members/{user_id}/tier`` — admin/owner. */
export async function updateOrgMemberTier(
  userId: string,
  body: UpdateMemberTierRequest,
): Promise<{ readonly ok: true; readonly usage_multiplier_override: number | null }> {
  return apiClient.put(
    `/org/members/${encodeURIComponent(userId)}/tier`,
    body,
  );
}

/** ``DELETE /org/members/{user_id}`` — admin/owner. */
export async function removeOrgMember(
  userId: string,
): Promise<{ readonly status: "removed" }> {
  return apiClient.delete(`/org/members/${encodeURIComponent(userId)}`);
}

/** ``GET /org/invites`` — admin/owner. */
export async function listOrgInvites(): Promise<readonly OrgInvite[]> {
  return apiClient.get<readonly OrgInvite[]>("/org/invites");
}

/** ``DELETE /org/invites/{invite_id}`` — admin/owner. */
export async function cancelOrgInvite(
  inviteId: string,
): Promise<{ readonly status: "cancelled" }> {
  return apiClient.delete(`/org/invites/${encodeURIComponent(inviteId)}`);
}
